import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileDoneOutlined, ProfileOutlined, ReadOutlined, SettingOutlined } from '@ant-design/icons';
import { Steps } from 'antd';
import classNames from 'classnames';
import { useDevice } from '../../../hooks/useDevice';
import { MODEL, QUERY_TYPE_LABEL } from '../api';
import ConfigurationStep from './ConfigurationStep';
import OutlineReviewStep from './OutlineReviewStep';
import PreviewStep from './PreviewStep';
import ReferenceStep from './ReferenceStep';
import { clampWordCount, DEFAULT_CONTENTS, normalizeQueryType } from './utils';
import styles from './index.module.scss';

/** 步骤编号 */
const STEP = { CONFIG: 1, REFERENCE: 2, OUTLINE: 3, PREVIEW: 4 };

const STEPS = [
    { key: STEP.CONFIG, title: '需求配置', desc: '填写标题与生成参数', icon: <SettingOutlined /> },
    { key: STEP.REFERENCE, title: '参考资料', desc: '选择参考文献（选填）', icon: <ReadOutlined /> },
    { key: STEP.OUTLINE, title: '确认大纲', desc: '查看并调整文档大纲', icon: <ProfileOutlined /> },
    { key: STEP.PREVIEW, title: '预览全文', desc: '查看生成结果', icon: <FileDoneOutlined /> },
];

/** 影响大纲生成结果的参数；变了就不再复用旧大纲 */
const outlineSignature = ({ title, queryType, model, config, references }) =>
    JSON.stringify({
        title: (title || '').trim(),
        queryType,
        model,
        contents: [...(config.contents || [])].sort(),
        wordCount: config.wordCount,
        enableLevel3: Boolean(config.enableLevel3),
        references: (references || []).map((ref) => ref.id),
    });

/**
 * 写作四步流程：需求配置 → 参考资料 → 确认大纲 → 预览全文（预览步由 PreviewStep 负责）。
 *
 * @param {string} title 首页输入的题目
 * @param {'PAPER'|'COURSE_PAPER'|'OPENING_REPORT'|'LITERATURE_REVIEW'} serviceType 首页选的写作类型
 * @param {number} wordCount 首页选的篇幅
 * @param {'normal'|'academic'} model 生成引擎（与首页开关共用，由上层持有）
 * @param {(model) => void} onModelChange
 * @param {({ title, serviceType }) => void} [onBack] 从需求配置返回上一页；不传则不显示「上一步」
 * @param {boolean} [embedded] 直接嵌在首页：第一步不显示顶部标题，写作类型随首页 Tab 同步
 * @param {(serviceType) => void} [onServiceTypeChange] 需求配置里改了写作类型
 * @param {(step) => void} [onStepChange] 当前步骤变化（首页据此切换布局）
 */
export default function StepForm({
    title,
    serviceType,
    wordCount,
    model,
    onModelChange,
    onBack,
    embedded = false,
    onServiceTypeChange,
    onStepChange,
}) {
    const { isMobile, screenWidth } = useDevice();
    // 步骤条的手机样式按宽度生效（_theme.scss 的 mobileSteps），标签排列也按宽度定，桌面浏览器缩窄窗口时两边才一致
    const compactSteps = isMobile || screenWidth <= 768;
    const [currentStep, setCurrentStep] = useState(STEP.CONFIG);
    const [docTitle, setDocTitle] = useState(title || '');

    // 需求配置 + 参考资料的选择（退回前面的步骤时原样传回）
    const [config, setConfig] = useState(() => {
        const type = normalizeQueryType(serviceType);
        return {
            serviceType: type,
            wordCount: clampWordCount(type, wordCount),
            contents: DEFAULT_CONTENTS,
            contentsTouched: false,
            enableLevel3: false,
            customReferences: [],
            recommendEnglishLiterature: false,
        };
    });
    const [references, setReferences] = useState([]);
    // 参考资料步的 AI 推荐结果：{ title, results }，标题没变时来回切换不重复检索
    const [aiReferenceCache, setAiReferenceCache] = useState(null);
    // 已生成 / 编辑过的大纲：{ sig, outline }，生成参数不变时复用
    const [outlineCache, setOutlineCache] = useState(null);
    // 已创建的订单：{ quote, sig, payload, outlines, model }
    const [order, setOrder] = useState(null);

    const queryType = config.serviceType;
    const effectiveModel = model === MODEL.ACADEMIC ? MODEL.ACADEMIC : MODEL.NORMAL;

    const genSig = useMemo(
        () =>
            outlineSignature({
                title: docTitle,
                queryType,
                model: effectiveModel,
                config,
                references,
            }),
        [docTitle, queryType, effectiveModel, config, references],
    );

    const handleOutlineChange = useCallback(
        (outline) => {
            setOutlineCache((prev) => {
                if (!outline) return prev ? null : prev;
                if (prev && prev.outline === outline && prev.sig === genSig) return prev;
                return { sig: genSig, outline };
            });
        },
        [genSig],
    );

    // 切换步骤时回到顶部
    useEffect(() => {
        window.scrollTo({ top: 0 });
        onStepChange?.(currentStep);
    }, [currentStep]);

    // 外部（首页 Tab）切换写作类型：同步到需求配置，不重建表单
    useEffect(() => {
        const type = normalizeQueryType(serviceType);
        setConfig((prev) => (prev.serviceType === type ? prev : { ...prev, serviceType: type }));
    }, [serviceType]);

    const handleServiceTypeChange = (type) => {
        setConfig((prev) => (prev.serviceType === type ? prev : { ...prev, serviceType: type }));
        onServiceTypeChange?.(type);
    };

    // 标题 / 类型 / 元素 / 篇幅 / 层级 / 引擎 / 参考资料变了，签名随之变化，确认大纲步会重新生成大纲
    const handleConfigNext = (next) => {
        const { title: nextTitle, ...rest } = next;
        setConfig((prev) => ({ ...prev, ...rest }));
        if (nextTitle) setDocTitle(nextTitle);
        setCurrentStep(STEP.REFERENCE);
    };

    const saveReferenceSelection = ({
        references: nextRefs,
        customReferences,
        recommendEnglishLiterature,
    }) => {
        setReferences(Array.isArray(nextRefs) ? nextRefs : []);
        setConfig((prev) => ({
            ...prev,
            customReferences: Array.isArray(customReferences) ? customReferences : [],
            recommendEnglishLiterature: Boolean(recommendEnglishLiterature),
        }));
    };

    const handleReferenceNext = (selection) => {
        saveReferenceSelection(selection);
        setCurrentStep(STEP.OUTLINE);
    };

    // 返回需求配置时保留已选的参考资料
    const handleReferencePrev = (selection) => {
        saveReferenceSelection(selection);
        setCurrentStep(STEP.CONFIG);
    };

    const handleOutlineComplete = (result) => {
        setOrder(result);
        setCurrentStep(STEP.PREVIEW);
    };

    const handleConfigPrev = () => {
        onBack?.({ title: docTitle, serviceType: config.serviceType });
    };

    // 步骤条沿用原首页的 antd Steps（手机上四步平分宽度、竖排标签）；
    // 手机首页第一步不显示步骤条（进入参考资料等后续步骤后再显示紧凑版），桌面不变
    const renderStepIndicator = () => {
        if (isMobile && embedded && currentStep === STEP.CONFIG) return null;
        return (
            <div className={classNames('mb-2 w-full md:mb-10 md:px-20', styles.steps)}>
                <Steps
                    current={currentStep - 1}
                    labelPlacement={compactSteps ? 'vertical' : 'horizontal'}
                    responsive={false}
                    items={STEPS.map((step) => ({
                        key: step.key,
                        title: step.title,
                        disabled: true,
                    }))}
                />
            </div>
        );
    };

    const renderStepContent = () => {
        if (currentStep === STEP.CONFIG) {
            return (
                <ConfigurationStep
                    title={title}
                    initialTitle={docTitle}
                    initialServiceType={config.serviceType}
                    initialWordCount={config.wordCount}
                    initialContents={config.contents}
                    initialContentsTouched={config.contentsTouched}
                    initialEnableLevel3={config.enableLevel3}
                    model={effectiveModel}
                    onModelChange={onModelChange}
                    onTitleChange={setDocTitle}
                    onServiceTypeChange={handleServiceTypeChange}
                    onNext={handleConfigNext}
                    onPrev={onBack ? handleConfigPrev : undefined}
                    embedded={embedded}
                />
            );
        }
        if (currentStep === STEP.REFERENCE) {
            return (
                <ReferenceStep
                    title={docTitle}
                    initialReferences={references}
                    initialCustomReferences={config.customReferences}
                    initialRecommendEnglishLiterature={config.recommendEnglishLiterature}
                    aiCache={aiReferenceCache}
                    onAiCacheChange={setAiReferenceCache}
                    onNext={handleReferenceNext}
                    onPrev={handleReferencePrev}
                />
            );
        }
        if (currentStep === STEP.OUTLINE) {
            return (
                <div className={styles.cardContent}>
                    <OutlineReviewStep
                        title={docTitle}
                        queryType={queryType}
                        model={effectiveModel}
                        onModelChange={onModelChange}
                        wordCount={config.wordCount}
                        contents={config.contents}
                        enableLevel3={config.enableLevel3}
                        references={references}
                        recommendEnglishLiterature={config.recommendEnglishLiterature}
                        cachedOutline={outlineCache?.sig === genSig ? outlineCache.outline : null}
                        onOutlineChange={handleOutlineChange}
                        existingOrder={order}
                        onComplete={handleOutlineComplete}
                        onPrev={() => setCurrentStep(STEP.REFERENCE)}
                    />
                </div>
            );
        }
        if (currentStep === STEP.PREVIEW && order?.quote) {
            return (
                <PreviewStep
                    quote={order.quote}
                    title={docTitle}
                    queryType={queryType}
                    model={order.model || effectiveModel}
                    outlines={order.outlines}
                    createPayload={order.payload}
                    onBack={() => setCurrentStep(STEP.OUTLINE)}
                    onQuoteChange={(quote) =>
                        setOrder((prev) => (prev ? { ...prev, quote } : prev))
                    }
                />
            );
        }
        return null;
    };

    return (
        <div
            className={classNames(styles.container, {
                [styles.homeForm]: embedded && currentStep === STEP.CONFIG,
            })}
        >
            {/* 首页第一步上方已有大标题与类型 Tab，不再重复 */}
            {embedded && currentStep === STEP.CONFIG ? null : (
                <div className={styles.formHeader}>
                    <h2 className={classNames(styles.formTitle, { [styles.empty]: !docTitle })}>
                        {docTitle || '未命名文档 · 请在下方填写标题'}
                    </h2>
                    <p className={styles.formSubtitle}>
                        <span className={styles.typeTag}>{QUERY_TYPE_LABEL[queryType]}</span>
                        {effectiveModel === MODEL.ACADEMIC
                            ? '超级自研学术特调模型正在为您深度规划专业文档'
                            : '正在为您智能生成专业文档'}
                    </p>
                </div>
            )}

            {renderStepIndicator()}

            <div key={currentStep} className={styles.stepContent}>
                {renderStepContent()}
            </div>
        </div>
    );
}
